import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Plus,
  Search,
  Globe,
  Smartphone,
  ShieldCheck,
  ExternalLink,
  Edit2,
  Trash2,
  Star,
  CheckCircle2,
  XCircle,
  X,
  Save,
  Filter,
  Layers,
  ArrowUpDown,
  Building2,
} from 'lucide-react';
import { OfficialPortfolioProject, ProjectType } from '../../types';
import { realtimeDbService } from '../../services/realtimeDbService';
import { INITIAL_OFFICIAL_PORTFOLIO } from '../../data/officialPortfolioData';
import { AlertDialog } from '../common/AlertDialog';
import {
  AdminFormField,
  AdminInput,
  AdminTextarea,
  AdminSelect,
  AdminLabel,
} from './ui/AdminForm';

export const AdminPortfolioManager: React.FC = () => {
  const [projects, setProjects] = useState<OfficialPortfolioProject[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [featuredOnly, setFeaturedOnly] = useState<boolean>(false);

  // Modal states
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingProject, setEditingProject] = useState<OfficialPortfolioProject | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: string; title: string; loading: boolean }>({
    open: false,
    id: '',
    title: '',
    loading: false,
  });

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    category: '',
    description: '',
    url: '',
    thumbnail: '',
    featured: false,
    projectType: 'website' as ProjectType,
    technologies: '',
    clientName: '',
    status: 'ACTIVE' as 'ACTIVE' | 'PASSIVE',
    sortOrder: 1,
  });

  const loadProjects = async () => {
    setIsLoading(true);
    try {
      const data = await realtimeDbService.getOfficialPortfolioProjects();
      setProjects(data && data.length > 0 ? data : INITIAL_OFFICIAL_PORTFOLIO);
    } catch (err) {
      console.warn('Error loading portfolio:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({
      title: '',
      slug: '',
      category: 'Kurumsal Web',
      description: '',
      url: '',
      thumbnail: '',
      featured: false,
      projectType: 'website',
      technologies: 'React, SEO, Mobil Uyumlu',
      clientName: '',
      status: 'ACTIVE',
      sortOrder: projects.length + 1,
    });
    setErrorMessage(null);
    setIsModalOpen(true);
  };

  const openEditModal = (proj: OfficialPortfolioProject) => {
    setEditingProject(proj);
    setFormData({
      title: proj.title,
      slug: proj.slug,
      category: proj.category,
      description: proj.description,
      url: proj.url || '',
      thumbnail: proj.thumbnail || '',
      featured: proj.featured,
      projectType: proj.projectType,
      technologies: proj.technologies ? proj.technologies.join(', ') : '',
      clientName: proj.clientName || '',
      status: proj.status,
      sortOrder: proj.sortOrder || 1,
    });
    setErrorMessage(null);
    setIsModalOpen(true);
  };

  const handleToggleFeatured = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await realtimeDbService.toggleOfficialPortfolioFeatured(id);
      await loadProjects();
    } catch (err) {
      console.error('Toggle featured error:', err);
    }
  };

  const handleToggleStatus = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await realtimeDbService.toggleOfficialPortfolioStatus(id);
      await loadProjects();
    } catch (err) {
      console.error('Toggle status error:', err);
    }
  };

  const handleDelete = (id: string, title: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialog({
      open: true,
      id,
      title,
      loading: false,
    });
  };

  const handleExecuteDelete = async () => {
    if (!deleteDialog.id || deleteDialog.loading) return;
    setDeleteDialog((prev) => ({ ...prev, loading: true }));
    try {
      await realtimeDbService.deleteOfficialPortfolioProject(deleteDialog.id);
      await loadProjects();
      setDeleteDialog({ open: false, id: '', title: '', loading: false });
    } catch (err) {
      console.error('Delete error:', err);
      setDeleteDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setErrorMessage('Lütfen proje başlığı giriniz.');
      return;
    }

    setIsSaving(true);
    setErrorMessage(null);

    try {
      const generatedSlug =
        formData.slug.trim() ||
        formData.title
          .toLowerCase()
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ş/g, 's')
          .replace(/ı/g, 'i')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');

      const parsedTechs = formData.technologies
        ? formData.technologies
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
        : [];

      const projectToSave: OfficialPortfolioProject = {
        id: editingProject ? editingProject.id : `port_${Date.now()}_${Math.random().toString(36).substr(2, 4)}`,
        title: formData.title.trim(),
        slug: generatedSlug,
        category: formData.category.trim() || 'Genel',
        description: formData.description.trim(),
        url: formData.url.trim() ? formData.url.trim() : undefined,
        thumbnail: formData.thumbnail.trim() ? formData.thumbnail.trim() : undefined,
        featured: formData.featured,
        projectType: formData.projectType,
        technologies: parsedTechs,
        isOfficialPortfolio: true,
        status: formData.status,
        sortOrder: Number(formData.sortOrder) || 1,
        clientName: formData.clientName.trim() || undefined,
        createdAt: editingProject ? editingProject.createdAt : Date.now(),
        updatedAt: Date.now(),
      };

      await realtimeDbService.saveOfficialPortfolioProject(projectToSave);
      setIsModalOpen(false);
      await loadProjects();
    } catch (err: any) {
      setErrorMessage(err?.message || 'Proje kaydedilirken bir hata oluştu.');
    } finally {
      setIsSaving(false);
    }
  };

  // Filtered list
  const filteredProjects = projects.filter((p) => {
    const matchesSearch =
      p.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.url && p.url.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesType = typeFilter === 'ALL' || p.projectType === typeFilter;
    const matchesFeatured = !featuredOnly || p.featured;

    return matchesSearch && matchesType && matchesFeatured;
  });

  const totalWebsites = projects.filter((p) => p.projectType === 'website').length;
  const totalMobile = projects.filter((p) => p.projectType === 'mobile_app').length;
  const totalCustom = projects.filter((p) => p.projectType === 'custom_software').length;
  const totalFeatured = projects.filter((p) => p.featured).length;

  return (
    <div className="space-y-6">
      {/* Header Banner & Action */}
      <div className="bg-white rounded-3xl p-6 sm:p-7 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
            <span>Adiosoft Resmi Portfolyo Yönetimi</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-black text-slate-900">
            Adiosoft Tarafından Gerçekleştirilen Projeler
          </h2>
          <p className="text-xs sm:text-sm text-slate-600">
            Tanıtım sitesinde ve kurumsal vitrinde sergilenen gerçek müşteri referansları, canlı web projeleri ve mobil uygulamalar.
          </p>
        </div>

        <button
          type="button"
          onClick={openCreateModal}
          className="px-5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md shadow-blue-600/20 transition flex items-center gap-2 whitespace-nowrap active:scale-95"
        >
          <Plus className="w-4 h-4" />
          <span>Yeni Proje Ekle</span>
        </button>
      </div>

      {/* Summary KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Toplam Proje</span>
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Layers className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 mt-2">{projects.length}</div>
          <span className="text-[11px] text-slate-500">Veritabanında kayıtlı</span>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Web Siteleri</span>
            <div className="w-8 h-8 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center">
              <Globe className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 mt-2">{totalWebsites}</div>
          <span className="text-[11px] text-emerald-600 font-medium">Canlı domain bağlantılı</span>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Mobil & Yazılım</span>
            <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
              <Smartphone className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 mt-2">{totalMobile + totalCustom}</div>
          <span className="text-[11px] text-slate-500">{totalMobile} Mobil, {totalCustom} Kurumsal</span>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-500">Öne Çıkanlar</span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
              <Star className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-black text-slate-900 mt-2">{totalFeatured}</div>
          <span className="text-[11px] text-amber-600 font-medium">Ana sayfada ilk sırada</span>
        </div>
      </div>

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row items-center justify-between gap-3">
        <div className="flex items-center gap-2.5 w-full md:w-80 px-3 py-2 bg-slate-50 rounded-xl border border-slate-200">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Proje veya kategori ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-transparent text-xs text-slate-900 placeholder:text-slate-400 outline-none font-medium"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="text-slate-400 hover:text-slate-600">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl text-xs font-semibold">
            <button
              onClick={() => setTypeFilter('ALL')}
              className={`px-3 py-1.5 rounded-lg transition ${
                typeFilter === 'ALL' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Tümü ({projects.length})
            </button>
            <button
              onClick={() => setTypeFilter('website')}
              className={`px-3 py-1.5 rounded-lg transition ${
                typeFilter === 'website' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Web ({totalWebsites})
            </button>
            <button
              onClick={() => setTypeFilter('mobile_app')}
              className={`px-3 py-1.5 rounded-lg transition ${
                typeFilter === 'mobile_app' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Mobil ({totalMobile})
            </button>
            <button
              onClick={() => setTypeFilter('custom_software')}
              className={`px-3 py-1.5 rounded-lg transition ${
                typeFilter === 'custom_software' ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              Kurumsal ({totalCustom})
            </button>
          </div>

          <button
            onClick={() => setFeaturedOnly(!featuredOnly)}
            className={`px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 border transition ${
              featuredOnly
                ? 'bg-amber-50 text-amber-800 border-amber-300'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <Star className={`w-3.5 h-3.5 ${featuredOnly ? 'fill-amber-500 text-amber-500' : 'text-slate-400'}`} />
            <span>Öne Çıkanlar</span>
          </button>
        </div>
      </div>

      {/* Projects Table List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/80 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <th className="py-3.5 px-4">Proje</th>
                <th className="py-3.5 px-4">Tür & Kategori</th>
                <th className="py-3.5 px-4">Bağlantı (URL)</th>
                <th className="py-3.5 px-4 text-center">Öne Çıkan</th>
                <th className="py-3.5 px-4 text-center">Durum</th>
                <th className="py-3.5 px-4 text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-xs text-slate-800">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-500 font-medium">
                    Projeler yükleniyor...
                  </td>
                </tr>
              ) : filteredProjects.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-slate-400 font-medium">
                    Arama kriterine uygun proje bulunamadı.
                  </td>
                </tr>
              ) : (
                filteredProjects.map((p) => {
                  const isWeb = p.projectType === 'website';
                  const isMobile = p.projectType === 'mobile_app';
                  const isCustom = p.projectType === 'custom_software';

                  return (
                    <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                      {/* Proje adı ve görsel */}
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl overflow-hidden bg-slate-100 border border-slate-200 flex-shrink-0 flex items-center justify-center">
                            {p.thumbnail ? (
                              <img src={p.thumbnail} alt={p.title} className="w-full h-full object-cover" />
                            ) : isMobile ? (
                              <Smartphone className="w-5 h-5 text-blue-500" />
                            ) : isCustom ? (
                              <Building2 className="w-5 h-5 text-indigo-500" />
                            ) : (
                              <Globe className="w-5 h-5 text-cyan-500" />
                            )}
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 hover:text-blue-600 transition">
                              {p.title}
                            </div>
                            <div className="text-[11px] text-slate-500 font-mono">
                              /{p.slug}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Tür ve Kategori */}
                      <td className="py-3.5 px-4">
                        <div className="space-y-1">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                              isWeb
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : isMobile
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                            }`}
                          >
                            {isWeb && <Globe className="w-3 h-3" />}
                            {isMobile && <Smartphone className="w-3 h-3" />}
                            {isCustom && <ShieldCheck className="w-3 h-3" />}
                            <span>
                              {isWeb ? 'Web Sitesi' : isMobile ? 'Mobil Uygulama' : 'Kurumsal Yazılım'}
                            </span>
                          </span>
                          <div className="text-[11px] text-slate-600 font-medium">
                            {p.category}
                          </div>
                        </div>
                      </td>

                      {/* URL */}
                      <td className="py-3.5 px-4">
                        {p.url ? (
                          <a
                            href={p.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 font-mono text-[11px] hover:underline"
                          >
                            <span className="truncate max-w-[180px]">{p.url.replace(/^https?:\/\//, '')}</span>
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        ) : (
                          <span className="text-slate-400 italic text-[11px]">Özel İç Altyapı</span>
                        )}
                      </td>

                      {/* Öne Çıkan Toggle */}
                      <td className="py-3.5 px-4 text-center">
                        <button
                          type="button"
                          onClick={(e) => handleToggleFeatured(p.id, e)}
                          title={p.featured ? 'Öne çıkanlardan kaldır' : 'Öne çıkar'}
                          className={`p-1.5 rounded-lg border transition ${
                            p.featured
                              ? 'bg-amber-50 text-amber-600 border-amber-200 hover:bg-amber-100'
                              : 'bg-slate-50 text-slate-300 border-slate-200 hover:text-amber-500'
                          }`}
                        >
                          <Star className={`w-4 h-4 ${p.featured ? 'fill-amber-400' : ''}`} />
                        </button>
                      </td>

                      {/* Durum Toggle */}
                      <td className="py-3.5 px-4 text-center">
                        <button
                          type="button"
                          onClick={(e) => handleToggleStatus(p.id, e)}
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold transition ${
                            p.status === 'ACTIVE'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                              : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                          }`}
                        >
                          {p.status === 'ACTIVE' ? (
                            <>
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              <span>Aktif</span>
                            </>
                          ) : (
                            <>
                              <XCircle className="w-3 h-3 text-slate-400" />
                              <span>Pasif</span>
                            </>
                          )}
                        </button>
                      </td>

                      {/* İşlemler */}
                      <td className="py-3.5 px-4 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => openEditModal(p)}
                            title="Düzenle"
                            className="p-1.5 rounded-lg text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleDelete(p.id, p.title, e)}
                            title="Sil"
                            className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create / Edit Project Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl w-full max-w-2xl overflow-hidden my-8">
            {/* Modal Header */}
            <div className="p-5 sm:p-6 border-b border-slate-200 flex items-center justify-between bg-slate-50">
              <div>
                <h3 className="text-base sm:text-lg font-bold text-slate-900">
                  {editingProject ? 'Referans Projesini Düzenle' : 'Yeni Referans Projesi Ekle'}
                </h3>
                <p className="text-xs text-slate-500">
                  Adiosoft vitrininde ve tanıtım sitesinde sergilenecek gerçek proje detayları.
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSave} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
              {errorMessage && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 font-semibold">
                  {errorMessage}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AdminFormField label="Proje Başlığı" required>
                  <AdminInput
                    type="text"
                    required
                    placeholder="Örn: Dalaman Cam Filmi"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  />
                </AdminFormField>

                <AdminFormField label="Slug (URL Kısa Adı)" helperText="Boş bırakılırsa başlıktan otomatik üretilir">
                  <AdminInput
                    type="text"
                    placeholder="Örn: dalamancamfilmi"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  />
                </AdminFormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AdminFormField label="Proje Türü" required>
                  <AdminSelect
                    value={formData.projectType}
                    onChange={(e) => setFormData({ ...formData, projectType: e.target.value as ProjectType })}
                  >
                    <option value="website">Canlı Web Sitesi (website)</option>
                    <option value="mobile_app">Özel Mobil Uygulama (mobile_app)</option>
                    <option value="google_business">Google Harita & İşletme (google_business)</option>
                    <option value="custom_software">Özel Yazılım & Entegrasyon (custom_software)</option>
                  </AdminSelect>
                </AdminFormField>

                <AdminFormField label="Sektör / Kategori" required>
                  <AdminInput
                    type="text"
                    required
                    placeholder="Örn: Oto & Cam Filmi, Yangın Güvenliği"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  />
                </AdminFormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <AdminFormField label="Canlı Web Sitesi URL" helperText="Varsa tam adres (https://...)">
                  <AdminInput
                    type="url"
                    placeholder="https://www.ornek.com"
                    value={formData.url}
                    onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  />
                </AdminFormField>

                <AdminFormField label="Müşteri / Kurum Adı">
                  <AdminInput
                    type="text"
                    placeholder="Örn: ESFA Yangın A.Ş."
                    value={formData.clientName}
                    onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  />
                </AdminFormField>
              </div>

              <AdminFormField label="Kapak Görseli URL (Thumbnail)">
                <AdminInput
                  type="url"
                  placeholder="https://images.unsplash.com/... veya doğrudan URL"
                  value={formData.thumbnail}
                  onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                />
              </AdminFormField>

              <AdminFormField label="Proje Açıklaması">
                <AdminTextarea
                  rows={3}
                  placeholder="Projenin amacı, Adiosoft tarafından sunulan çözümler ve sağlanan fayda..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </AdminFormField>

              <AdminFormField label="Kullanılan Teknolojiler" helperText="Virgülle ayırarak yazın (Örn: React, SEO, Çoklu Dil)">
                <AdminInput
                  type="text"
                  placeholder="Örn: React, Mobil Uyumlu, SEO Altyapısı"
                  value={formData.technologies}
                  onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
                />
              </AdminFormField>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2 border-t border-slate-100">
                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="feat-checkbox"
                    checked={formData.featured}
                    onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500 border-slate-300"
                  />
                  <label htmlFor="feat-checkbox" className="text-xs font-bold text-slate-800 cursor-pointer">
                    Öne Çıkan Proje
                  </label>
                </div>

                <AdminFormField label="Görünüm Durumu">
                  <AdminSelect
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'ACTIVE' | 'PASSIVE' })}
                  >
                    <option value="ACTIVE">Aktif (Yayında)</option>
                    <option value="PASSIVE">Pasif (Gizli)</option>
                  </AdminSelect>
                </AdminFormField>

                <AdminFormField label="Sıralama Önceliği">
                  <AdminInput
                    type="number"
                    min={1}
                    value={formData.sortOrder}
                    onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 1 })}
                  />
                </AdminFormField>
              </div>

              {/* Modal Actions */}
              <div className="pt-4 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 transition"
                >
                  Vazgeç
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs shadow-md shadow-blue-600/20 transition flex items-center gap-2 disabled:opacity-50"
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>{isSaving ? 'Kaydediliyor...' : 'Projeyi Kaydet'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation AlertDialog */}
      <AlertDialog
        open={deleteDialog.open}
        title="Referans Projesini Silmek İstediğinize Emin Misiniz?"
        description={`"${deleteDialog.title}" referans projesi resmi portföyden kalıcı olarak silinecektir. Bu işlem geri alınamaz.`}
        variant="destructive"
        confirmText={deleteDialog.loading ? 'Siliniyor...' : 'Evet, Sil'}
        cancelText="Vazgeç"
        loading={deleteDialog.loading}
        onConfirm={handleExecuteDelete}
        onCancel={() => {
          if (!deleteDialog.loading) {
            setDeleteDialog({ open: false, id: '', title: '', loading: false });
          }
        }}
      />
    </div>
  );
};
